import Cookie from 'js-cookie';
import qs from 'query-string';
import { get } from 'lodash-es';

import { CURRENT_COUNTRY_CHANGED, emitter } from '../event-bus';

/**
 *
 */
export default class CurrentCountry {
  HASH_PARAMETER = 'setCurrentScope';
  HIDDEN_COUNTRY_FIELD_SELECTOR = '#hidden-country-field';
  COOKIE_NAME = 'currentCountry';
  static COUNTRY_REVEAL_CONTAINER = '.country-reveal-container';

  hiddenCountryField: HTMLElement;
  defaultCountryLabel: string;
  defaultCountryCode: string;

  constructor() {
    this.hiddenCountryField = document.querySelector(this.HIDDEN_COUNTRY_FIELD_SELECTOR);
    this.readDefaultCountryAttributesFromEl();
    this.bindHashChangeListener();
    this.updateFromHash();
  }

  /**
   * Copy the data-attributes (defaultCountryLabel, etc.) stored on the element
   */
  readDefaultCountryAttributesFromEl() {
    this.defaultCountryLabel = this.hiddenCountryField?.getAttribute('data-default-country-label');
    this.defaultCountryCode = this.hiddenCountryField?.getAttribute('data-default-country-code');
  }

  /**
   * Update the visibility of country-specific information (such as prices)
   * TODO(raphaelsaunier): Consider decoupling reveal logic and extracting it into separate class
   * @param countryCode
   */
  reveal(countryCode) {
    Array.from(document.querySelectorAll(CurrentCountry.COUNTRY_REVEAL_CONTAINER)).forEach((container) => {
      this.revealCountryForElement(countryCode, container);
    });

    // The tax label on the product page
    document.querySelectorAll('.product-page-prices .price-from--prices').forEach((priceContainer) => {
      Array.from(priceContainer.children).forEach((localisation) => {
        const taxLabel = localisation.querySelector('.product-page-taxes');
        if (taxLabel) {
          if (localisation.attributes.getNamedItem('data-country-code')?.value === countryCode) {
            taxLabel.classList.remove('hidden');
          } else {
            taxLabel.classList.add('hidden');
          }
        }
      });
    });
  }

  revealCountryForElement(countryCode, container) {
    Array.from(container.querySelectorAll('[data-country-code]')).forEach((el: HTMLElement) => {
      if (el.dataset.countryCode.toUpperCase() === countryCode.toUpperCase()) {
        const productBox = <HTMLDivElement>el.closest('.product.box');
        if (productBox) {
          const priceEl = el.querySelector('span[data-price="value"]');
          productBox.dataset.price = priceEl ? priceEl.innerHTML : '0';
        }
        return el.classList.remove('hidden');
      }
      return el.classList.add('hidden');
    });
  }

  /**
   * Listen for changes to the location hash
   */
  bindHashChangeListener() {
    window.addEventListener('hashchange', this.updateFromHash);
  }

  /**
   *
   * @param code
   * @returns {boolean}
   */
  isInCorrectLocalisation(code) {
    return window.Phoenix.countriesInLocalisation.includes(code);
  }

  /**
   * Clear the currentCountry cookie
   */
  clearCookie = () => Cookie.remove(this.COOKIE_NAME);

  /**
   * Set the currentCountry cookie with an lifetime (set in days)
   * @param val
   * @param {number} expires
   * @param {boolean} secure
   */
  setCookie = (val, expires = 30, secure = true) => Cookie.set(this.COOKIE_NAME, val, { expires, secure });

  update() {
    const code = this.getFromHash() || Cookie.get(this.COOKIE_NAME) || this.defaultCountryCode;
    if (!code) {
      return;
    }
    this.updateCurrentCountry(code.toUpperCase());
  }

  updateFromHash = () => {
    const codeFromHash = this.getFromHash();
    if (codeFromHash && this.isInCorrectLocalisation(codeFromHash)) {
      this.updateCurrentCountry(codeFromHash);
      history.replaceState({}, '', location.pathname + location.search);
    }
  };

  updateCurrentCountry(code) {
    if (!this.isInCorrectLocalisation(code)) {
      code = this.defaultCountryCode;
    }
    code = code.toUpperCase();
    const label = this.getLabelFromCode(code) || this.defaultCountryLabel;
    this.setCookie(code);
    this.updateSelectedCountry(label, code);
    this.reveal(code);
    window.Phoenix.currentCountry = code;
    emitter.emit(CURRENT_COUNTRY_CHANGED, code.toUpperCase());
  }

  /**
   * Return the country from the #setCurrentScope=xx location hash
   * @returns {string|null}
   */
  getFromHash() {
    const value = qs.parse(location.hash)[this.HASH_PARAMETER];

    return (Array.isArray(value) ? value[0] : value) || null;
  }

  /**
   * Get the translated country name to use as a button label
   * @param code
   * @returns {string|false}
   */
  getLabelFromCode(code) {
    return get(this.getCountries(), [code], false);
  }

  /**
   * Get the countries available in the country selector
   * @returns {Object}
   */
  getCountries() {
    return window.Phoenix.countries || {};
  }

  updateSelectedCountry(label, code) {
    Array.from(document.querySelectorAll('.country-selected-label')).forEach((el) => {
      el.innerHTML = label;
    });
    Array.from(document.querySelectorAll('.country-selected-code')).forEach((el) => {
      el.innerHTML = code;
    });
  }
}
